import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import SolidInfinityIcon from 'LuxKit/Icons/solid/SolidInfinityIcon'
import Caption from 'LuxKit/Typography/Caption'
import Sheet from 'components/utils/Sheet'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import getLoyaltyActionRewardProgress from 'loyalty/lib/getLoyaltyActionRewardProgress'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const STATUS_SIZE_PX = 40
const RING_STROKE = 3
/** Matches `LoyaltyAccountTileCircularProgressBar` outer stroke when `strokeWidth` is set (1px inset each side). */
const COMPLETED_SIZE_PX = STATUS_SIZE_PX - 2

const StatusSlot = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(STATUS_SIZE_PX)};
  height: ${rem(STATUS_SIZE_PX)};
  flex-shrink: 0;
`

const StatusCircle = styled(Sheet)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(COMPLETED_SIZE_PX)};
  height: ${rem(COMPLETED_SIZE_PX)};
  flex-shrink: 0;
  box-sizing: border-box;
`

/** Keeps text visually centred within the progress ring */
const ProgressCaption = styled(Caption)`
  display: block;
  width: 100%;
  line-height: 1;
  text-align: center;
`

interface Props {
  reward: App.LoyaltyActionReward
  tier: App.LoyaltyTier
}

function LoyaltyActionRewardsStatusIndicator({ reward, tier }: Props) {
  const intl = useIntl()
  const progress = getLoyaltyActionRewardProgress(reward)

  if (progress.isFullyComplete) {
    return (
      <StatusSlot>
        <StatusCircle
          background={`tier-${tier}-dark`}
          borderRadius="round"
        >
          <LineCheckIcon size="XS" />
        </StatusCircle>
      </StatusSlot>
    )
  }

  if (progress.kind === 'unlimited') {
    return (
      <LoyaltyAccountTileCircularProgressBar
        progress={progress.percent}
        size={STATUS_SIZE_PX}
        strokeWidth={RING_STROKE}
        tier={tier}
        variant="light"
      >
        <SolidInfinityIcon size="XS" colour="neutral-two" />
      </LoyaltyAccountTileCircularProgressBar>
    )
  }

  // one_time + capped: same SVG ring (Sheet CSS borders can't match stroke/inset/centering)
  const fractionLabel = intl.formatMessage(
    {
      id: 'nvpQXWzVH5WD5dBAdAVX',
      defaultMessage: '{current}/{target}',
      description: 'Action reward completion fraction in the status ring (e.g. 2/4)',
    },
    { current: progress.appliedCount, target: progress.target },
  )

  return (
    <LoyaltyAccountTileCircularProgressBar
      progress={progress.percent}
      size={STATUS_SIZE_PX}
      strokeWidth={RING_STROKE}
      tier={tier}
      variant="light"
    >
      <ProgressCaption variant="small" weight="bold" colour="neutral-two">
        {fractionLabel}
      </ProgressCaption>
    </LoyaltyAccountTileCircularProgressBar>
  )
}

export default LoyaltyActionRewardsStatusIndicator
